import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { business, pages } from "../data/business";

// Convert "9:00 AM" / "7:00 PM" → "09:00" / "19:00"
function to24(t: string) {
  const [time, period] = t.split(" ");
  const [hStr, mStr] = time.split(":");
  let hh = Number(hStr);
  if (period === "PM" && hh !== 12) hh += 12;
  if (period === "AM" && hh === 12) hh = 0;
  return `${String(hh).padStart(2, "0")}:${mStr}`;
}

// Legacy: schema.org openingHours string format (e.g. "Sa 09:00-19:00").
// Kept for back-compat with older parsers; the structured
// openingHoursSpecification below is the preferred form.
function buildOpeningHours() {
  const dayMap: Record<string, string> = {
    Monday: "Mo", Tuesday: "Tu", Wednesday: "We", Thursday: "Th",
    Friday: "Fr", Saturday: "Sa", Sunday: "Su",
  };
  const lines: string[] = [];
  business.hours.forEach((h) => {
    if (h.closed || !h.open || !h.close) return;
    lines.push(`${dayMap[h.day]} ${to24(h.open)}-${to24(h.close)}`);
  });
  return lines;
}

// Structured schema.org OpeningHoursSpecification — richer than the legacy
// string and explicit about closed days. Times are local (Eastern Time —
// America/New_York); schema.org has no first-class timezone field, but the
// physical address (Benton, TN) anchors the locale for consumers.
function buildOpeningHoursSpec() {
  const dayUrl: Record<string, string> = {
    Monday: "https://schema.org/Monday",
    Tuesday: "https://schema.org/Tuesday",
    Wednesday: "https://schema.org/Wednesday",
    Thursday: "https://schema.org/Thursday",
    Friday: "https://schema.org/Friday",
    Saturday: "https://schema.org/Saturday",
    Sunday: "https://schema.org/Sunday",
  };
  return business.hours.map((h) => {
    if (h.closed || !h.open || !h.close) {
      // Per schema.org guidance, model closed days with equal opens/closes.
      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: dayUrl[h.day],
        opens: "00:00",
        closes: "00:00",
      };
    }
    return {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: dayUrl[h.day],
      opens: to24(h.open),
      closes: to24(h.close),
    };
  });
}

export function SchemaJsonLd() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Venues that run their own line from their own address (today: Blue Hole
    // BBQ) must not have the lodge's org node emitted on their page — it would
    // put The Outfitter's number in the DOM of a page that is not The
    // Outfitter, and misattribute the lodge's hours and geo to the venue.
    // Those routes get their own entity instead, linked to the org by @id.
    const slug = pathname === "/" ? "" : pathname.replace(/\/+$/, "").slice(1);
    const venue = pages[slug];

    const orgData = {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "@id": "https://ocoeeriverexperience.com/#org",
      name: business.name,
      description:
        "Whitewater rafting on the Ocoee, plus on-site lodging, dining, and Cherokee National Forest hikes near the Ocoee River — host of the 1996 Olympic whitewater events. Benton, TN.",
      url: "https://ocoeeriverexperience.com/",
      telephone: business.phoneRaw,
      email: business.email,
      image: "https://ocoeeriverexperience.com/ocoee-seal.png",
      address: {
        "@type": "PostalAddress",
        streetAddress: business.address.street,
        addressLocality: business.address.city,
        addressRegion: business.address.state,
        postalCode: business.address.zip,
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: business.geo.lat,
        longitude: business.geo.lng,
      },
      openingHours: buildOpeningHours(),
      openingHoursSpecification: buildOpeningHoursSpec(),
      areaServed: ["Benton TN", "Cleveland TN", "Chattanooga TN", "Atlanta GA"],
      // aggregateRating intentionally omitted until verified review counts are confirmed.
      sameAs: [
        business.social.instagram,
        business.social.facebook,
        business.social.twitter,
        business.social.tiktok,
      ],
    };

    // A venue with its own line gets its own node instead of the org's, so
    // its page never carries another business's phone, hours or geo.
    const venueData = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "@id": `https://ocoeeriverexperience.com/${slug}#venue`,
      name: venue?.title,
      description: venue?.subtitle,
      url: `https://ocoeeriverexperience.com/${slug}`,
      telephone: venue?.phoneRawOverride,
      // Plain-text address: schema.org accepts Text for `address`, and we only
      // have the venue's address as a single verified string.
      address: venue?.addressOverride,
      // Hours and geo intentionally omitted — the values we hold are the lodge
      // campus's and would be wrong here. `pages.bbq` flags hours as
      // NEEDS-VERIFY, so we publish nothing rather than guess.
      parentOrganization: { "@id": "https://ocoeeriverexperience.com/#org" },
    };

    const data = venue?.phoneRawOverride ? venueData : orgData;

    // New round #92: breadcrumb structured data
    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://ocoeeriverexperience.com/" },
        { "@type": "ListItem", position: 2, name: "Rafting", item: "https://ocoeeriverexperience.com/rafting" },
        { "@type": "ListItem", position: 3, name: "Lodging", item: "https://ocoeeriverexperience.com/lodge" },
      ],
    };

    const inject = (id: string, payload: unknown) => {
      document.head.querySelector(`#${id}`)?.remove();
      const tag = document.createElement("script");
      tag.id = id;
      tag.type = "application/ld+json";
      tag.text = JSON.stringify(payload);
      document.head.appendChild(tag);
      return tag;
    };

    const businessTag = inject("schema-localbusiness", data);
    const crumbsTag   = inject("schema-breadcrumbs", breadcrumbs);

    return () => {
      businessTag.remove();
      crumbsTag.remove();
    };
  }, [pathname]);

  return null;
}
